import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader, useExport } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable, type DataTableHandle } from "@dl/web-core-js/table";
import {
  Badge,
  Button,
  cn,
  Dialog,
  Field,
  MenuButton,
  Segmented,
  Select,
  Textarea,
  useConfirm,
  type MenuItem,
} from "@dl/web-core-js/ui";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Package, Pencil, Plus, RefreshCw, Store, Trash2, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ResourceClient } from "@dl/web-core-js/api";
import {
  imageUrl,
  LazyProductClient,
  netQuantity,
  PRODUCT_EXPORTS,
  PRODUCT_STATUSES,
  PRODUCT_TYPES,
  ProductClient,
  rankLabel,
  salesCad,
  salesUsd,
  units,
  WarehouseClient,
  warehouseNetQuantity,
  type LazyProductDTO,
  type NamedDTO,
  type ProductDTO,
  type ProductType,
  type SalesWindow,
  type SigmFieldMask,
  type SigmSyncData,
  type SigmSyncOutcome,
  type SigmSyncResult,
} from "../api/product";
import { DescriptionsTab } from "./product/DescriptionsTab";
import { GeneralTab } from "./product/GeneralTab";
import { NexpimView } from "../views";
import { STATUS_VARIANT, TYPE_VARIANT } from "./product/badges";
import { ShopifyClient, shopifyAdminProductUrl, type ShopifyListingDTO, type ShopifyPublicationTarget } from "../api/shopify";
import { FinanceReportDialogs, type FinanceReport } from "./reports/FinanceReportDialogs";
import { ProductLink } from "../components/ProductLink";

/**
 * A link that is the whole grid cell: the negative margins take back the
 * cell's own padding so there is no dead border to miss, and the hover tints
 * the text rather than underlining a block.
 */
const CELL_LINK = "-mx-3 flex h-full items-center gap-1.5 px-3 hover:no-underline [&:hover_span]:text-accent";
import { DispatchBulkDialog } from "./product/DispatchBulkDialog";
import { InventoryRuleBulkDialog } from "./product/InventoryRuleBulkDialog";
import { TagBulkDialog, type TagBulkMode } from "./product/TagBulkDialog";
import { familyRows, isFamily, rowRemainingWeeks, rowWeeklyAvg, type ProductRow } from "./product/families";

/**
 * The product catalogue.
 *
 * The widest grid in the suite, and the one that most needs the column-priority
 * mechanism: seventeen-odd columns on a desktop, four meaningful ones on a
 * phone, from a single definition.
 *
 * Header badges are computed over the *filtered* rows rather than the whole
 * set, so narrowing to a category answers "what is this category worth" instead
 * of repeating a constant.
 */
export function ProductsPage() {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { start: startExport, busy } = useExport();

  const client = useMemo(() => new LazyProductClient(http), [http]);
  // Writes and documents live on /product; the grid reads from /product/lazy.
  const products = useMemo(() => new ProductClient(http), [http]);
  const shopify = useMemo(() => new ShopifyClient(http), [http]);
  const confirm = useConfirm();
  const allowed = hasView(NexpimView.PRODUCTS);

  const [selected, setSelected] = useState<LazyProductDTO[]>([]);
  const [importErrors, setImportErrors] = useState<string[] | null>(null);
  const [importing, setImporting] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [report, setReport] = useState<FinanceReport | null>(null);
  const [tagging, setTagging] = useState<TagBulkMode | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const grid = useRef<DataTableHandle | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // Flat, or one row per family with its products underneath. Remembered per
  // browser like the grid's own state: the family view is a way of reading
  // the catalogue, and someone who prefers it prefers it every time.
  const [view, setView] = useState<ProductsView>(() => storedView());
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch {
      // A preference that cannot be remembered still works for this session.
    }
  }, [view]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["product", "lazy", "all"] });

  /**
   * Retailer feeds all take the selected product ids and answer with a file, so
   * they collapse into one menu driven by the format table.
   *
   * The two catalogue-wide reports sit in their own section because they ignore
   * the selection entirely — grouping them with the rest would imply otherwise.
   */
  const exportItems = useMemo<MenuItem[]>(() => {
    const ids = selected.map((row) => row.id);
    const sectionSelection = t("export.section.selection", "For selected products");

    const items: MenuItem[] = PRODUCT_EXPORTS.map((format) => ({
      label: t(format.labelKey, format.slug) + (format.zip ? " (zip)" : ""),
      section: sectionSelection,
      disabled: ids.length === 0 || busy !== null,
      onClick: () =>
        void startExport(
          format.slug,
          () => products.exportDocument(format.slug, ids),
          t("job.download.failed", "Download failed"),
        ),
    }));

    items.push({
      label: t("export.inventory-data", "Inventory data"),
      section: t("export.section.catalogue", "Whole catalogue"),
      disabled: busy !== null,
      onClick: () =>
        void startExport(
          "inventory-data",
          () => products.exportInventoryData(),
          t("job.download.failed", "Download failed"),
        ),
    });

    const sectionReports = t("export.section.reports", "Finance reports");
    const reports: Array<[FinanceReport, string]> = [
      ["sales", t("export.report.sales", "Sales report")],
      ["commissions", t("export.report.commissions", "Commissions")],
      ["royalty", t("export.report.royalty", "Designer royalty")],
      ["cogs", t("export.report.cogs", "COGS")],
    ];
    for (const [kind, label] of reports) {
      items.push({
        label,
        section: sectionReports,
        disabled: busy !== null,
        onClick: () => setReport(kind),
      });
    }

    return items;
  }, [selected, busy, startExport, products, t]);

  /**
   * What nexpim remembers per variation group — the gid and the opt-out — is
   * enough for the column and costs no Shopify call. Publications and the
   * product's live state are on the product's Shopify tab.
   */
  const listings = useQuery({
    queryKey: ["shopify", "listed"],
    queryFn: () => shopify.listed(),
    staleTime: 5 * 60_000,
  });
  const store = useQuery({
    queryKey: ["shopify", "store"],
    queryFn: () => shopify.store(),
    staleTime: 60 * 60_000,
    retry: false,
  });
  const listingByGroup = useMemo(() => {
    const map = new Map<number, ShopifyListingDTO>();
    for (const listing of listings.data ?? []) map.set(listing.variation_group, listing);
    return map;
  }, [listings.data]);
  const refreshShopify = () => queryClient.invalidateQueries({ queryKey: ["shopify"] });

  /**
   * Shopify actions, addressed by the selected products; the server resolves
   * each to its variation group. Sync and match are jobs; the rest answer
   * right away and refresh the column.
   */
  const shopifyItems = useMemo<MenuItem[]>(() => {
    const ids = selected.map((row) => row.id);
    const none = ids.length === 0;
    const sectionSelection = t("shopify.menu.section.selection", "For selected products");
    const sectionCatalogue = t("shopify.menu.section.catalogue", "Whole catalogue");
    const call = (label: string, work: () => Promise<unknown>) => async () => {
      try {
        await work();
        await refreshShopify();
        toast.success(label);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : t("error.generic", "Request failed"));
      }
    };
    const publish = (target: ShopifyPublicationTarget, published: boolean): MenuItem => ({
      label:
        (published ? t("shopify.menu.publish", "Publish to") : t("shopify.menu.unpublish", "Unpublish from")) +
        " " +
        (target === "ONLINE_STORE"
          ? t("shopify.publication.online-store", "Online Store")
          : target === "CA"
            ? t("shopify.publication.ca", "Canada market")
            : t("shopify.publication.us", "United States market")),
      section: sectionSelection,
      disabled: none,
      onClick: () =>
        void call(published ? t("shopify.published", "Published") : t("shopify.unpublished", "Unpublished"), () =>
          shopify.setPublished(ids, target, published),
        )(),
    });
    return [
      {
        label: t("shopify.menu.sync", "Create / update on Shopify"),
        section: sectionSelection,
        disabled: none || busy !== null,
        onClick: () => void startExport("shopify-sync", () => shopify.sync(ids), t("shopify.sync.failed", "Sync failed")),
      },
      publish("ONLINE_STORE", true),
      publish("CA", true),
      publish("US", true),
      publish("ONLINE_STORE", false),
      publish("CA", false),
      publish("US", false),
      {
        label: t("shopify.menu.archive", "Archive on Shopify"),
        section: sectionSelection,
        disabled: none,
        onClick: async () => {
          if (
            await confirm({
              title: t("shopify.archive.title", "Archive on Shopify?"),
              message: t("shopify.archive.message", "The product goes off every managed publication and its status becomes ARCHIVED. Orders and reviews stay."),
              variant: "danger",
              confirmLabel: t("shopify.archive.confirm", "Archive"),
            })
          ) {
            await call(t("shopify.archived", "Archived on Shopify"), () => shopify.archive(ids))();
          }
        },
      },
      {
        label: t("shopify.menu.delete", "Delete from Shopify"),
        section: sectionSelection,
        disabled: none,
        onClick: async () => {
          if (
            await confirm({
              title: t("shopify.delete.title", "Delete from Shopify?"),
              message: t("shopify.delete.message", "The Shopify product is deleted for good; nexpim forgets it and a later sync creates a new one."),
              consequences: [
                t("shopify.delete.consequence.reviews", "Its reviews and its place in orders' history are lost."),
                t("shopify.delete.consequence.url", "Its URL stops working."),
              ],
              variant: "danger",
              requireText: "DELETE",
            })
          ) {
            await call(t("shopify.deleted", "Deleted from Shopify"), () => shopify.remove(ids))();
          }
        },
      },
      {
        label: t("shopify.menu.match", "Match existing Shopify products"),
        section: sectionCatalogue,
        disabled: busy !== null,
        onClick: () => void startExport("shopify-match", () => shopify.match(), t("shopify.match.failed", "Match failed")),
      },
      {
        label: t("shopify.menu.archive-unlinked.preview", "Preview archiving unlinked products"),
        section: sectionCatalogue,
        disabled: busy !== null,
        onClick: () =>
          void startExport(
            "shopify-archive-unlinked-preview",
            () => shopify.archiveUnlinked(true),
            t("shopify.archive-unlinked.failed", "Archiving unlinked products failed"),
          ),
      },
      {
        label: t("shopify.menu.archive-unlinked", "Archive unlinked products"),
        section: sectionCatalogue,
        disabled: busy !== null,
        onClick: async () => {
          if (
            await confirm({
              title: t("shopify.archive-unlinked.title", "Archive every unlinked store product?"),
              message: t(
                "shopify.archive-unlinked.message",
                "Every store product the last match did not tie to a group is archived: the duplicates of a group another product holds, and the products whose SKUs are all discontinued. Run the match and the preview first.",
              ),
              consequences: [
                t("shopify.archive-unlinked.consequence.reviews", "Their reviews go out of view with them."),
                t("shopify.archive-unlinked.consequence.url", "Their URL is redirected to the product of their group when there is one; otherwise it stops working."),
              ],
              variant: "danger",
              requireText: "ARCHIVE",
            })
          ) {
            void startExport(
              "shopify-archive-unlinked",
              () => shopify.archiveUnlinked(false),
              t("shopify.archive-unlinked.failed", "Archiving unlinked products failed"),
            );
          }
        },
      },
      {
        label: t("products.images.repair-extensions", "Repair image file names"),
        section: sectionCatalogue,
        disabled: busy !== null,
        onClick: async () => {
          try {
            const planned = await products.repairImageExtensions(true);
            if (planned.length === 0) {
              toast.success(t("products.images.repair-extensions.none", "Every image file name has its extension"));
              return;
            }
            const ok = await confirm({
              title: t("products.images.repair-extensions.title", "Rename {{count}} image file(s)?", { count: planned.length }),
              message: t(
                "products.images.repair-extensions.message",
                "These product images and thumbnails were stored without an extension, which Shopify refuses. Each file is renamed in storage to end with the extension of its type, and nexpim follows.",
              ),
              consequences: planned.slice(0, 8),
              variant: "primary",
            });
            if (!ok) return;
            const done = await products.repairImageExtensions(false);
            const failed = done.filter((line) => line.startsWith("FAILED"));
            if (failed.length === 0) {
              toast.success(t("products.images.repair-extensions.done", "{{count}} file(s) renamed", { count: done.length }));
            } else {
              toast.error(failed.join(" · "));
            }
          } catch (failure) {
            toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed"));
          }
        },
      },
      {
        label: t("shopify.menu.cleanup-handles.preview", "Preview product URL cleanup"),
        section: sectionCatalogue,
        disabled: busy !== null,
        onClick: () =>
          void startExport(
            "shopify-cleanup-handles-preview",
            () => shopify.cleanupHandles(true),
            t("shopify.cleanup-handles.failed", "Product URL cleanup failed"),
          ),
      },
      {
        label: t("shopify.menu.cleanup-handles", "Clean product URLs"),
        section: sectionCatalogue,
        disabled: busy !== null,
        onClick: async () => {
          if (
            await confirm({
              title: t("shopify.cleanup-handles.title", "Clean every product URL?"),
              message: t(
                "shopify.cleanup-handles.message",
                "Every product nexpim lists on Shopify gets the URL its title reads as (hamilton-70-tv-stand-with-4-storage-cabinets). Run the preview first.",
              ),
              consequences: [
                t("shopify.cleanup-handles.consequence.redirect", "Each old URL is redirected to the new one, so links and search results keep working."),
                t("shopify.cleanup-handles.consequence.archived", "An archived product holding a wanted URL moves to <url>-archived first; a URL held by another live product is left alone and listed."),
              ],
              variant: "danger",
              requireText: "URLS",
            })
          ) {
            void startExport(
              "shopify-cleanup-handles",
              () => shopify.cleanupHandles(false),
              t("shopify.cleanup-handles.failed", "Product URL cleanup failed"),
            );
          }
        },
      },
      {
        label: t("shopify.menu.sync-all", "Sync everything"),
        section: sectionCatalogue,
        disabled: busy !== null,
        onClick: async () => {
          if (
            await confirm({
              title: t("shopify.sync-all.title", "Sync every group to Shopify?"),
              message: t("shopify.sync-all.message", "Every variation group with a sellable product is created or updated. Groups with a problem are skipped and listed in the job."),
              variant: "primary",
            })
          ) {
            void startExport("shopify-sync-all", () => shopify.syncAll(), t("shopify.sync.failed", "Sync failed"));
          }
        },
      },
      {
        label: t("shopify.menu.store", "Store info"),
        section: sectionCatalogue,
        onClick: () => void navigate({ to: "/shopify/store" }),
      },
    ];
  }, [selected, busy, startExport, shopify, confirm, navigate, t]);

  const runImport = async (file: File) => {
    setImporting(true);
    try {
      const errors = await products.importFeed(file);
      await refresh();
      // An empty error list is the success case; a non-empty one still means
      // the rows that parsed were imported, so this is a report, not a failure.
      if (errors.length === 0) {
        toast.success(t("products.import.done", "Import complete"));
      } else {
        setImportErrors(errors);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Import failed"));
    } finally {
      setImporting(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["product", "lazy", "all"],
    queryFn: () => client.findAll(),
    enabled: allowed,
    // The full catalogue is expensive to fetch; don't refetch it on every
    // focus change while the user is moving between screens.
    staleTime: 5 * 60_000,
  });

  const selectedRows = useMemo(() => {
    const ids = new Set(selected.map((row) => row.id));
    return (data ?? []).filter((row) => ids.has(row.id));
  }, [selected, data]);

  // The family view is built from the same loaded catalogue; the parents are
  // synthetic rows (see families.ts) and never reach the selection.
  const rows = useMemo<ProductRow[]>(() => (view === "families" ? familyRows(data ?? []) : (data ?? [])), [data, view]);

  /**
   * Reference lists power the multi-select filters. They are small and cached
   * hard — the alternative, faceting from the loaded rows, would silently drop
   * a category no visible product happens to use.
   */
  const categories = useQuery({
    queryKey: ["product", "category"],
    queryFn: () => new ResourceClient<NamedDTO>(http, "/product/category").findAll(),
    enabled: allowed,
    staleTime: 30 * 60_000,
  });
  const collections = useQuery({
    queryKey: ["product", "collection"],
    queryFn: () => new ResourceClient<NamedDTO>(http, "/product/collection").findAll(),
    enabled: allowed,
    staleTime: 30 * 60_000,
  });
  const tags = useQuery({
    queryKey: ["product", "tag"],
    queryFn: () => new ResourceClient<NamedDTO>(http, "/product/tag").findAll(),
    enabled: allowed,
    staleTime: 30 * 60_000,
  });

  /** NEXCA / NEXUS ids, for the CA·US split under the available quantity. */
  const warehouses = useQuery({
    queryKey: ["warehouse"],
    queryFn: () => new WarehouseClient(http).findAll(),
    enabled: allowed,
    staleTime: 30 * 60_000,
  });

  const warehouseCa = (warehouses.data ?? []).find((entry) => entry.code === "NEXCA")?.id ?? null;
  const warehouseUs = (warehouses.data ?? []).find((entry) => entry.code === "NEXUS")?.id ?? null;

  const cols = useMemo(() => {
    const c = defineColumns<ProductRow>();
    const named = (list: NamedDTO[] | undefined) =>
      (list ?? [])
        .map((entry) => ({ value: String(entry.id), label: entry.name ?? `#${entry.id}` }))
        .sort((a, b) => a.label.localeCompare(b.label));

    /** Units over a window with its CAD and USD money underneath. */
    const familyCaption = (row: ProductRow) => {
      const family = row.family;
      if (!family) return "";
      const parts = [t("products.family.count", "{{count}} products", { count: family.members.length })];
      if (family.colours > 0) parts.push(t("products.family.colours", "{{count}} colours", { count: family.colours }));
      if (family.sizes > 0) parts.push(t("products.family.sizes", "{{count}} sizes", { count: family.sizes }));
      return parts.join(" · ");
    };
    const blank = <span className="text-ink-faint"></span>;

    // In the family view the rank sits on the units line of the window it is
    // computed from — "#12 · 345" reads as one fact — and ranks families and
    // single products together, by units added up (see families.ts). A
    // product's own sales rank is not per window: it is on the velocity cell.
    const salesCell = (row: ProductRow, window: SalesWindow) => {
      const rank = row.ranks?.[window];
      return (
      <span className="flex flex-col leading-tight">
        <span className="tabular-nums">
          {rank ? <span className="mr-1 text-[11px] font-medium text-accent">{`#${rank}`}</span> : null}
          {units(row.kpi_sales, window)}
        </span>
        <span className="text-[11px] text-ink-faint">
          {`${money(salesCad(row.kpi_sales, window))} CAD`}
        </span>
        <span className="text-[11px] text-ink-faint">
          {`${money(salesUsd(row.kpi_sales, window))} USD`}
        </span>
      </span>
      );
    };

    return [
      c.custom(
        "image",
        // The header filter on this column is the NEW/MATURE split, which is
        // what the Vaadin grid put here too.
        (row) => ((row.kpi_sales?.first_sale_aging_days ?? 0) <= 183 ? "NEW" : "MATURE"),
        (row) => {
          const url = imageUrl(row.image);
          return url ? (
            <img
              src={url}
              alt=""
              loading="lazy"
              // The bucket can 404 or the object can be missing; an empty box
              // reads better than a broken-image glyph in every row.
              onError={(event) => {
                event.currentTarget.style.visibility = "hidden";
              }}
              className="size-12 rounded object-cover"
            />
          ) : (
            <span className="block size-12 rounded bg-surface-sunk" />
          );
        },
        {
          headerKey: "column.image",
          filter: "select",
          filterOptions: [
            { value: "NEW", label: "NEW" },
            { value: "MATURE", label: "MATURE" },
          ],
          enableSorting: false,
          size: 64,
        },
      ),

      // Identity: the SKU carries the UPC and the "new launch" flag, so a
      // buyer can tell a fresh listing from an established one at a glance.
      //
      // The SKU and name cells are the links into the product; the row itself
      // is not. A row that navigated on any click made ticking a checkbox or
      // copying a UPC a matter of aim, and a real link can be opened in a new
      // tab, which a click handler cannot. The link fills its cell, padding
      // included, so it is as easy to hit as the row used to be.
      c.custom(
        "sku",
        // What the search matches on this column: the SKU, the UPC and the HS code.
        (row) => `${row.metadata?.sku ?? ""} ${row.metadata?.upc ?? ""} ${row.metadata?.harmonized_code ?? ""}`,
        (row) =>
          isFamily(row) ? (
            <span className="text-[11px] text-ink-muted">{familyCaption(row)}</span>
          ) : (
          <ProductLink id={row.id} className={CELL_LINK}>
            <span className="flex flex-col leading-tight">
              <span className="font-medium text-ink">{row.metadata?.sku}</span>
              <span className="text-[11px] text-ink-muted">{row.metadata?.upc}</span>
            </span>
            {(row.kpi_sales?.first_sale_aging_days ?? 0) <= 183 && (
              <Badge variant="success" size="sm" pill>
                NEW
              </Badge>
            )}
          </ProductLink>
          ),
        { headerKey: "column.sku", priority: 1 },
      ),
      c.custom(
        "name",
        (row) => row.description_en?.product_name ?? "",
        (row) => {
          if (isFamily(row)) {
            return (
              <span className="flex min-w-0 flex-col leading-tight">
                <span className="truncate font-semibold text-ink">{row.description_en?.product_name}</span>
                <span className="truncate text-[11px] text-ink-muted">{t("products.family.label", "Family")}</span>
              </span>
            );
          }
          const variation = (row.variations ?? []).map((entry) => entry.value).join(" - ");
          return (
            <ProductLink id={row.id} className={CELL_LINK}>
              <span className="flex min-w-0 flex-col leading-tight">
                <span className="truncate font-medium text-ink">
                  {row.description_en?.product_name}
                </span>
                <span className="truncate text-[11px] text-ink-muted">
                  {variation === "" ? "no variation data" : variation}
                </span>
              </span>
            </ProductLink>
          );
        },
        { headerKey: "column.name", priority: 2, size: 300 },
      ),
      // Colour and size are variation values, not columns of their own in
      // Vaadin — they lived only as filters under the name. Here they are
      // hidden columns so their filters remain reachable via "show all".
      c.text(
        "color",
        (row) =>
          (row.variations ?? []).find((entry) => entry.type === "COLOR")?.value ?? "",
        { headerKey: "column.color", defaultVisible: false },
      ),
      c.text(
        "size",
        (row) => (row.variations ?? []).find((entry) => entry.type === "SIZE")?.value ?? "",
        { headerKey: "column.size", defaultVisible: false },
      ),

      c.custom(
        "type",
        (row) => row.type,
        (row) =>
          isFamily(row) ? blank : (
          <Badge variant={TYPE_VARIANT[row.type] ?? "neutral"} size="sm">
            {row.type}
          </Badge>
          ),
        {
          headerKey: "column.type",
          filter: "multiselect",
          filterOptions: PRODUCT_TYPES.map((entry) => ({ value: entry, label: entry })),
          priority: 4,
        },
      ),
      c.custom(
        "status",
        (row) => row.status,
        (row) =>
          isFamily(row) ? blank : (
          <Badge variant={STATUS_VARIANT[row.status] ?? "neutral"} size="sm">
            {row.status}
          </Badge>
          ),
        {
          headerKey: "column.status",
          filter: "multiselect",
          filterOptions: PRODUCT_STATUSES.map((entry) => ({ value: entry, label: entry })),
          priority: 3,
        },
      ),
      c.custom(
        "shopify",
        (row) => {
          const listing = row.variation_group == null ? undefined : listingByGroup.get(row.variation_group);
          if (row.shopify_excluded || listing?.excluded) return "EXCLUDED";
          return listing?.product_gid ? "LISTED" : "NONE";
        },
        (row) => {
          const listing = row.variation_group == null ? undefined : listingByGroup.get(row.variation_group);
          const excluded = row.shopify_excluded || listing?.excluded;
          const gid = listing?.product_gid;
          return (
            <span className="flex items-center gap-1">
              {excluded ? (
                <Badge variant="warning" size="sm">{t("shopify.status.excluded", "Excluded")}</Badge>
              ) : gid ? (
                <Badge variant="success" size="sm">{t("shopify.status.listed", "Listed")}</Badge>
              ) : (
                <span className="text-ink-faint"></span>
              )}
              {gid && store.data && (
                <a
                  href={shopifyAdminProductUrl(store.data.admin_url, gid)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  className="text-accent hover:underline text-[11px]"
                  title={t("shopify.link.admin", "Shopify admin")}
                >
                  ↗
                </a>
              )}
            </span>
          );
        },
        {
          headerKey: "column.shopify",
          filter: "multiselect",
          filterOptions: [
            { value: "LISTED", label: t("shopify.status.listed", "Listed") },
            { value: "NONE", label: t("shopify.status.not-listed", "Not on Shopify") },
            { value: "EXCLUDED", label: t("shopify.status.excluded", "Excluded") },
          ],
          priority: 4,
        },
      ),
      c.custom(
        "category",
        (row) => (row.category ? String(row.category.id) : ""),
        (row) => row.category?.name ?? "",
        {
          headerKey: "column.category",
          filter: "multiselect",
          filterOptions: named(categories.data),
          priority: 3,
        },
      ),
      c.custom(
        "collections",
        (row) => (row.collections ?? []).map((entry) => String(entry.id)),
        (row) => {
          if (isFamily(row)) return blank;
          const list = row.collections ?? [];
          return (
            <span className="flex flex-col leading-tight">
              <span className="truncate text-ink">
                {list.length === 0 ? "no collection" : list[0]?.name}
              </span>
              <span className="text-[11px] text-ink-muted">{list.length}</span>
            </span>
          );
        },
        {
          headerKey: "column.collections",
          filter: "multiselect",
          filterOptions: named(collections.data),
          enableSorting: false,
          priority: 5,
        },
      ),
      c.custom(
        "tags",
        (row) => (row.tags ?? []).map((entry) => String(entry.id)),
        (row) => {
          if (isFamily(row)) return blank;
          const list = row.tags ?? [];
          return (
            <span className="flex flex-col leading-tight">
              <span className="truncate text-ink">
                {list.length === 0 ? "no tags" : list[0]?.name}
              </span>
              <span className="text-[11px] text-ink-muted">{list.length}</span>
            </span>
          );
        },
        {
          headerKey: "column.tags",
          filter: "multiselect",
          filterOptions: named(tags.data),
          enableSorting: false,
          priority: 5,
        },
      ),

      // The number the grid opens on, so it reads a step above its neighbours.
      // Sorted on across types; the rank under it is within the product's own
      // type ("#3" among singles, "Bundle #3" among bundles), and only a
      // sellable product (available, pending launch) has one.
      c.custom(
        "sales_weekly_avg",
        (row) => truncate2(rowWeeklyAvg(row)),
        (row) => {
          const rank = row.family ? null : rankLabel(row, t);
          return (
            <span className="flex flex-col items-end leading-tight">
              <span className="text-[14px] font-semibold text-ink tabular-nums">{truncate2(rowWeeklyAvg(row))}</span>
              {rank && <span className="text-[11px] font-medium text-accent tabular-nums">{rank}</span>}
            </span>
          );
        },
        { headerKey: "column.sales-weekly-avg", filter: "range", align: "right", priority: 4 },
      ),
      // Inventory. Available is net of reservations *and* open order lines,
      // and turns red under 50 — the reorder trigger the planners watch.
      c.custom(
        "available_qty",
        (row) => netQuantity(row),
        (row) => {
          const net = netQuantity(row);
          return (
            <span className="flex flex-col leading-tight">
              <span className={net < 50 ? "font-bold text-risk tabular-nums" : "tabular-nums"}>
                {net}
              </span>
              <span className="text-[11px] text-ink-muted">
                {`CA ${warehouseNetQuantity(row, warehouseCa)} · US ${warehouseNetQuantity(row, warehouseUs)}`}
              </span>
            </span>
          );
        },
        { headerKey: "column.available-qty", filter: "range", align: "right", priority: 3 },
      ),
      c.number("in_prod_qty", (row) => row.kpi_inventory?.production_quantity ?? 0, {
        headerKey: "column.in-production-qty",
        filter: "range",
        priority: 4,
      }),
      c.custom(
        "remaining_weeks",
        (row) => rowRemainingWeeks(row),
        (row) => {
          const weeks = rowRemainingWeeks(row);
          return (
            <span className={weeks < 4 ? "font-bold text-risk tabular-nums" : "tabular-nums"}>
              {weeks}
            </span>
          );
        },
        {
          headerKey: "column.remaining-inventory-in-weeks",
          filter: "range",
          align: "right",
          priority: 4,
        },
      ),

      c.custom(
        "cogs",
        (row) => round(row.pricing?.cogs) ?? 0,
        (row) => (isFamily(row) ? blank : <span className="tabular-nums">{round(row.pricing?.cogs) ?? 0}</span>),
        { headerKey: "column.cogs", filter: "range", align: "right" },
      ),
      c.custom(
        "inventory_value",
        (row) => row.kpi_inventory?.inventory_value ?? 0,
        (row) => <span className="tabular-nums">{money(row.kpi_inventory?.inventory_value)}</span>,
        { headerKey: "column.inventory-value", filter: "range", align: "right" },
      ),
      c.custom(
        "percent_of_inventory",
        (row) => (row.kpi_inventory?.percent_of_inventory_quantity ?? 0) * 100,
        (row) => (
          <span className="tabular-nums">
            {`${((row.kpi_inventory?.percent_of_inventory_quantity ?? 0) * 100).toFixed(2)}%`}
          </span>
        ),
        {
          headerKey: "column.percent-of-inventory",
          filter: "range",
          align: "right",
          defaultVisible: false,
        },
      ),

      c.custom(
        "sales_4w",
        (row) => units(row.kpi_sales, "four_weeks"),
        (row) => salesCell(row, "four_weeks"),
        { headerKey: "column.sales.4-weeks", filter: "range", align: "right", priority: 5 },
      ),
      c.custom(
        "sales_12w",
        (row) => units(row.kpi_sales, "twelve_weeks"),
        (row) => salesCell(row, "twelve_weeks"),
        { headerKey: "column.sales.12-weeks", filter: "range", align: "right", priority: 4 },
      ),
      c.custom(
        "sales_ytd",
        (row) => units(row.kpi_sales, "ytd"),
        (row) => salesCell(row, "ytd"),
        { headerKey: "column.sales.ytd", filter: "range", align: "right", priority: 4 },
      ),
      c.custom(
        "sales_ytd_last_year",
        (row) => units(row.kpi_sales, "ytd_minus_1"),
        (row) => salesCell(row, "ytd_minus_1"),
        { headerKey: "column.sales.ytd-last-year", filter: "range", align: "right", priority: 5 },
      ),
      c.custom(
        "sales_last_year",
        (row) => units(row.kpi_sales, "year_minus_1"),
        (row) => salesCell(row, "year_minus_1"),
        {
          headerKey: "column.sales.last-year",
          filter: "range",
          align: "right",
          defaultVisible: false,
        },
      ),

      // The sales rank, for its range filter ("top 50"), behind "show all".
      // Singles and bundles are ranked apart, so filter on the type as well.
      c.number("sales_rank", (row) => (row.family ? null : row.kpi_sales?.sales_rank ?? null), {
        headerKey: "column.sales-rank",
        filter: "range",
        defaultVisible: false,
      }),

      c.custom(
        "sales_aging",
        (row) => row.kpi_sales?.last_sale_aging_days ?? 0,
        (row) => (
          <span className="flex flex-col leading-tight">
            <span className="tabular-nums">{row.kpi_sales?.last_sale_aging_days ?? 0}</span>
            <span className="text-[11px] text-ink-muted">{row.kpi_sales?.last_sale_date ?? ""}</span>
          </span>
        ),
        { headerKey: "column.sales.aging", filter: "range", defaultVisible: false },
      ),
      c.text("created_at", (row) => formatIsoDate(row.created_at), {
        headerKey: "column.creation-date",
        filter: "daterange",
        defaultVisible: false,
      }),
    ];
  }, [categories.data, collections.data, tags.data, warehouseCa, warehouseUs, listingByGroup, store.data, t]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.products", "Products")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title={t("navigation.item.products", "Products")} />
        <div className="p-4 lg:p-6">
          <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
            {t("error.generic", "Something went wrong. Please try again.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.products", "Products")}
        subtitle={t("products.subtitle", "The product catalogue.")}
      />

      <DataTable<ProductRow>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={isLoading}
        selectable
        onSelectionChange={setSelected}
        {...(view === "families" ? { getSubRows: (row: ProductRow) => row.family?.members, groupingParents: true } : {})}
        // Survives a trip into a product and back; cleared when the user picks
        // another section from the menu.
        stateKey="/products"

        // The grid opens on available sellable products, what sells most per
        // week first — a family's velocity being its members' added up, the
        // same order holds in both views.
        initialSorting={[{ id: "sales_weekly_avg", desc: true }]}
        initialFilters={[
          { id: "status", value: ["AVAILABLE"] },
          { id: "type", value: ["SINGLE_ITEM", "BUNDLE"] },
        ]}
        extraBadges={<InventoryBadges rows={data ?? []} />}
        toolbarLead={
          <Segmented
            value={view}
            onChange={setView}
            options={[
              { value: "flat" as const, label: t("products.view.flat", "Flat") },
              { value: "families" as const, label: t("products.view.families", "Families") },
            ]}
          />
        }
        // Four menus and nothing loose. Products is the lifecycle — create,
        // import, sync, delete — and shows only to accounts that may write;
        // Edit is what the selection can be given; Export and Shopify are the
        // outbound side.
        toolbarExtra={
          <>
            {canWrite && (
              <MenuButton
                label={t("products.menu", "Products")}
                icon={<Package className="size-4" />}
                items={[
                  {
                    label: t("products.menu.create", "New product"),
                    icon: <Plus className="size-4" />,
                    onClick: () => setCreating(true),
                  },
                  {
                    label: t("products.menu.import", "Import"),
                    icon: <Upload className="size-4" />,
                    disabled: importing,
                    onClick: () => fileInput.current?.click(),
                  },
                  {
                    // The sync creates products that are not in nexpim yet, so
                    // it never demands a selection; one just prefills the SKUs.
                    label: t("products.menu.sync", "Sync from SIGM"),
                    icon: <RefreshCw className="size-4" />,
                    onClick: () => setSyncOpen(true),
                  },
                  {
                    label: t("products.menu.delete", "Delete"),
                    icon: <Trash2 className="size-4" />,
                    section: t("products.menu.selection", "Selection"),
                    disabled: selected.length === 0,
                    onClick: () => void grid.current?.deleteSelected(),
                  },
                ]}
              />
            )}
            <MenuButton
              label={t("products.edit.menu", "Edit")}
              icon={<Pencil className="size-4" />}
              items={[
                {
                  label: t("products.tags.add", "Add tags"),
                  section: t("products.tags.menu", "Tags"),
                  disabled: selected.length === 0,
                  onClick: () => setTagging("add"),
                },
                {
                  label: t("products.tags.remove", "Remove tags"),
                  section: t("products.tags.menu", "Tags"),
                  disabled: selected.length === 0,
                  onClick: () => setTagging("remove"),
                },
                {
                  label: t("products.rules.menu", "Inventory rules"),
                  section: t("products.inventory.menu", "Inventory"),
                  disabled: selected.length === 0,
                  onClick: () => setRulesOpen(true),
                },
                {
                  label: t("products.dispatch.menu", "Dispatch warehouses"),
                  section: t("products.inventory.menu", "Inventory"),
                  disabled: selected.length === 0,
                  onClick: () => setDispatchOpen(true),
                },
              ]}
            />
            <MenuButton
              label={t("button.export", "Export")}
              icon={<Download className="size-4" />}
              items={exportItems}
            />
            <MenuButton label="Shopify" icon={<Store className="size-4" />} items={shopifyItems} />
          </>
        }
        hideDeleteButton
        handle={grid}
        onDeleteSelected={async (rows) => {
          await products.removeBatch(rows.map((row) => row.id));
          await refresh();
          toast.success(t("notification.deleted", "Deleted"));
        }}
        deleteSelectedConfirm={(count) => ({
          title: t("products.delete.title", "Delete {{count}} product(s)?", { count }),
          message: t("products.delete.message", "This permanently removes the products below."),
          consequences: [
            t("products.delete.consequence.data", "Pricing, COGS, images and metadata go with them."),
            t("products.delete.consequence.feeds", "Retailer feeds stop including them on the next export."),
            t("products.delete.consequence.undo", "There is no undo."),
          ],
          // Deliberately not translated: the phrase is a literal the user must
          // reproduce, and a locale switch mid-task must not change it.
          requireText: "DELETE PRODUCTS",
        })}
        emptyMessage={t("table.empty", "Nothing to show")}
      />

      {/* The import endpoint reads the raw body, so this is a plain file picker
          rather than a multipart form. */}
      <input
        ref={fileInput}
        type="file"
        accept=".xlsx,.xls,.csv"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void runImport(file);
        }}
      />

      <SigmSyncDialog
        open={syncOpen}
        onClose={() => setSyncOpen(false)}
        products={selected}
        client={products}
        onDone={refresh}
      />

      <CreateProductDialog
        open={creating}
        onClose={() => setCreating(false)}
        client={products}
        onCreated={async (created) => {
          await refresh();
          setCreating(false);
          void navigate({ to: "/products/$productId", params: { productId: String(created.id) } });
        }}
      />

      <FinanceReportDialogs report={report} onClose={() => setReport(null)} />

      {/* The selection is re-read from the loaded rows rather than passed as
          held: the grid only re-emits it when it changes, so after one
          write the held rows still carry yesterday's tags and the "N will
          change" count for a second write would be wrong. */}
      <TagBulkDialog
        open={tagging !== null}
        mode={tagging ?? "add"}
        products={selectedRows}
        tags={tags.data ?? []}
        onClose={() => setTagging(null)}
        onDone={() => void refresh()}
      />

      <InventoryRuleBulkDialog
        open={rulesOpen}
        productIds={selected.map((row) => row.id)}
        onClose={() => setRulesOpen(false)}
        onDone={() => void refresh()}
      />

      <DispatchBulkDialog
        open={dispatchOpen}
        products={selectedRows}
        onClose={() => setDispatchOpen(false)}
        onDone={() => void refresh()}
      />

      <Dialog
        open={importErrors !== null}
        onClose={() => setImportErrors(null)}
        title={t("products.import.errors", "Import finished with problems")}
        description={t(
          "products.import.errors.detail",
          "Rows that parsed were imported. The rest are listed below.",
        )}
        size="md"
        actions={
          <Button variant="primary" onClick={() => setImportErrors(null)}>
            {t("button.close", "Close")}
          </Button>
        }
      >
        <ul className="flex flex-col gap-1">
          {(importErrors ?? []).map((problem, index) => (
            <li key={index} className="rounded bg-surface-sunk px-2 py-1 text-[12px] text-ink-muted">
              {problem}
            </li>
          ))}
        </ul>
      </Dialog>
    </>
  );
}

/**
 * Pulls products back out of the SIGM ERP.
 *
 * Rebuilt around one idea: the dialog should say what it is about to do. The
 * old one offered nine checkboxes of which two were inert, replaced package
 * lists and rebuilt bundle compositions whether or not you asked, required a
 * selection even though the sync can create products, and reported the whole
 * run as a single "synced" or "failed".
 *
 * So the options are grouped by what they touch, the three that replace a
 * whole list are labelled as replacing it, and Preview runs the identical code
 * path with the writes switched off so you can read the consequences first.
 */
type SyncOptions = Omit<SigmSyncData, "sku" | "dry_run" | "item_fields">;

const FIELD_KEYS = ["name", "upc", "hs_code", "measurements", "variation", "category"] as const;
type FieldKey = (typeof FIELD_KEYS)[number];

/**
 * Defaults reproduce what the sync has always actually done, rather than what
 * the old checkboxes claimed. Packages and composition were replaced on every
 * run, so they start on; they are simply visible and switchable now.
 */
const SYNC_DEFAULTS: SyncOptions = {
  with_name: true,
  with_upc: true,
  with_hs_code: true,
  with_measurements: true,
  with_variation: false,
  with_category: false,
  create_missing: true,
  update_existing: true,
  with_packages: true,
  with_group: true,
  with_child_packages: true,
  with_create_items: true,
  with_update_items: false,
};

const OUTCOME_VARIANT: Record<SigmSyncOutcome, "success" | "info" | "neutral" | "error"> = {
  CREATED: "success",
  UPDATED: "info",
  UNCHANGED: "neutral",
  SKIPPED: "neutral",
  FAILED: "error",
};

function SigmSyncDialog({
  open,
  onClose,
  products,
  client,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  products: LazyProductDTO[];
  client: ProductClient;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<"preview" | "run" | null>(null);
  const [options, setOptions] = useState<SyncOptions>(SYNC_DEFAULTS);
  const [results, setResults] = useState<SigmSyncResult[] | null>(null);
  const [itemOverride, setItemOverride] = useState(false);
  const [itemFields, setItemFields] = useState<SigmFieldMask>({
    name: true,
    upc: true,
    hs_code: true,
    measurements: true,
    variation: false,
    category: false,
  });

  const selectedSkus = products
    .map((row) => row.metadata?.sku)
    .filter((sku): sku is string => Boolean(sku));

  const [skuText, setSkuText] = useState("");

  // The selection seeds the field, it does not replace it. Reopening the
  // dialog with a different selection refills it; typing over it is expected,
  // because a SKU that is not in nexpim yet cannot be selected in the grid.
  useEffect(() => {
    if (open) {
      setSkuText(selectedSkus.join(", "));
      setResults(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedSkus.join(",")]);

  const skuCount = skuText.split(/[,;\s]+/).filter(Boolean).length;

  const set = <K extends keyof SyncOptions>(key: K, value: SyncOptions[K]) =>
    setOptions((current) => ({ ...current, [key]: value }));

  const submit = async (dry_run: boolean) => {
    setBusy(dry_run ? "preview" : "run");
    try {
      const answer = await client.sigmSync({
        sku: skuText,
        dry_run,
        ...options,
        item_fields: itemOverride ? itemFields : null,
      });
      setResults(answer);

      if (!dry_run) {
        onDone();
        const failed = answer.filter((entry) => entry.outcome === "FAILED").length;
        if (failed === 0) {
          toast.success(t("products.sync.done", "Synced {{count}} SKU(s)", { count: answer.length }));
        } else {
          toast.warning(
            t("products.sync.partial", "{{failed}} of {{count}} SKU(s) failed", {
              failed,
              count: answer.length,
            }),
          );
        }
      }
    } catch {
      toast.error(t("products.sync.error", "The sync could not be run."));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("products.sync", "Sync from SIGM")}
      description={t(
        "products.sync.description",
        "Reads SIGM and writes into nexpim. Preview first to see exactly what would change.",
      )}
      size="lg"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy !== null}>
            {t("button.close", "Close")}
          </Button>
          <Button
            variant="secondary"
            loading={busy === "preview"}
            disabled={busy !== null || skuCount === 0}
            onClick={() => void submit(true)}
          >
            {t("products.sync.preview", "Preview")}
          </Button>
          <Button
            variant="primary"
            loading={busy === "run"}
            disabled={busy !== null || skuCount === 0}
            onClick={() => void submit(false)}
          >
            {t("products.sync.run", "Sync")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label={t("products.sync.skus", "SKUs")}
          hint={t(
            "products.sync.skus.hint",
            "Separate with commas, spaces or new lines. A SKU that is not in nexpim yet will be created.",
          )}
        >
          <Textarea
            rows={2}
            value={skuText}
            onChange={(event) => setSkuText(event.target.value)}
            placeholder={t("products.sync.skus.placeholder", "246088, 246089")}
          />
        </Field>

        <SyncSection title={t("products.sync.section.product", "The SKUs above")}>
          <SyncToggle
            label={t("products.sync.opt.create-missing", "Create the ones that are not in nexpim yet")}
            checked={options.create_missing}
            onChange={(value) => set("create_missing", value)}
          />
          <SyncToggle
            label={t("products.sync.opt.update-existing", "Update the ones that already exist")}
            checked={options.update_existing}
            onChange={(value) => set("update_existing", value)}
          />
          <FieldMaskRow
            disabled={!options.create_missing && !options.update_existing}
            value={{
              name: options.with_name,
              upc: options.with_upc,
              hs_code: options.with_hs_code,
              measurements: options.with_measurements,
              variation: options.with_variation,
              category: options.with_category,
            }}
            onChange={(key, value) =>
              set(
                (
                  {
                    name: "with_name",
                    upc: "with_upc",
                    hs_code: "with_hs_code",
                    measurements: "with_measurements",
                    variation: "with_variation",
                    category: "with_category",
                  } as const
                )[key],
                value,
              )
            }
          />
          <SyncToggle
            label={t("products.sync.opt.packages", "Replace the package list from SIGM")}
            note={t("products.sync.replaces", "Replaces the whole list")}
            checked={options.with_packages}
            onChange={(value) => set("with_packages", value)}
          />
        </SyncSection>

        <SyncSection
          title={t("products.sync.section.items", "Items inside a bundle")}
          note={t("products.sync.bundles-only", "Ignored for anything that is not a bundle in SIGM.")}
        >
          <SyncToggle
            label={t("products.sync.opt.create-items", "Create items that are not in nexpim yet")}
            checked={options.with_create_items}
            onChange={(value) => set("with_create_items", value)}
          />
          <SyncToggle
            label={t("products.sync.opt.update-items", "Update items that already exist")}
            checked={options.with_update_items}
            onChange={(value) => set("with_update_items", value)}
          />
          <SyncToggle
            label={t("products.sync.opt.item-fields", "Use different fields for items")}
            checked={itemOverride}
            onChange={setItemOverride}
          />
          {itemOverride && (
            <FieldMaskRow
              value={itemFields}
              onChange={(key, value) => setItemFields((current) => ({ ...current, [key]: value }))}
            />
          )}
          <SyncToggle
            label={t("products.sync.opt.child-packages", "Replace each item's package list from SIGM")}
            note={t("products.sync.replaces", "Replaces the whole list")}
            checked={options.with_child_packages}
            onChange={(value) => set("with_child_packages", value)}
          />
        </SyncSection>

        <SyncSection title={t("products.sync.section.composition", "Bundle composition")}>
          <SyncToggle
            label={t("products.sync.opt.group", "Rebuild the items and quantities from SIGM")}
            note={t(
              "products.sync.replaces-composition",
              "Replaces the whole list. An item that cannot be resolved is dropped from the bundle.",
            )}
            checked={options.with_group}
            onChange={(value) => set("with_group", value)}
          />
        </SyncSection>

        {results && <SyncResults results={results} />}
      </div>
    </Dialog>
  );
}

function SyncSection({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-line p-3">
      <h3 className="text-[13px] font-medium text-ink">{title}</h3>
      {note && <p className="mt-0.5 text-[12px] text-ink-muted">{note}</p>}
      <div className="mt-2 flex flex-col gap-1.5">{children}</div>
    </section>
  );
}

function SyncToggle({
  label,
  note,
  checked,
  onChange,
}: {
  label: string;
  note?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2 text-[13px] text-ink">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 size-3.5 shrink-0 accent-[var(--accent)]"
      />
      <span>
        {label}
        {note && <span className="ml-1.5 text-[12px] text-warn">{note}</span>}
      </span>
    </label>
  );
}

/** The six overwritable fields, on one line. */
function FieldMaskRow({
  value,
  onChange,
  disabled,
}: {
  value: SigmFieldMask;
  onChange: (key: FieldKey, next: boolean) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className={cn("flex flex-wrap gap-x-4 gap-y-1 pl-5", disabled && "pointer-events-none opacity-40")}>
      {FIELD_KEYS.map((key) => (
        <label key={key} className="flex cursor-pointer items-center gap-1.5 text-[12px] text-ink-muted">
          <input
            type="checkbox"
            checked={value[key]}
            onChange={(event) => onChange(key, event.target.checked)}
            className="size-3 accent-[var(--accent)]"
          />
          {t(`products.sync.field.${key.replace(/_/g, "-")}`, key)}
        </label>
      ))}
    </div>
  );
}

/** Per-SKU outcome, so a partial run reads as a partial run. */
function SyncResults({ results }: { results: SigmSyncResult[] }) {
  const { t } = useTranslation();
  const preview = results.some((entry) => entry.dry_run);

  return (
    <section className="rounded-lg border border-line bg-surface-sunk p-3">
      <h3 className="text-[13px] font-medium text-ink">
        {preview
          ? t("products.sync.preview.title", "What would change")
          : t("products.sync.result.title", "What changed")}
      </h3>
      <ul className="mt-2 flex max-h-64 flex-col gap-2 overflow-y-auto">
        {results.map((entry, index) => (
          <li key={`${entry.sku ?? "?"}:${index}`} className="rounded bg-surface px-2 py-1.5">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-medium text-ink">{entry.sku ?? "?"}</span>
              <Badge variant={OUTCOME_VARIANT[entry.outcome]} size="sm">
                {t(`products.sync.outcome.${entry.outcome}`, entry.outcome)}
              </Badge>
              {entry.failure && (
                <span className="text-[12px] text-ink-muted">
                  {t(`products.sync.failure.${entry.failure}`, entry.failure)}
                </span>
              )}
            </div>
            {entry.changes.length > 0 && (
              <ul className="mt-1 flex flex-col gap-0.5">
                {entry.changes.map((change, position) => (
                  <li
                    key={`${change.type}:${position}`}
                    className={cn("text-[12px]", change.warning ? "text-warn" : "text-ink-muted")}
                  >
                    {t(`products.sync.change.${change.type}`, {
                      defaultValue: change.type,
                      sku: change.sku ?? "",
                      count: change.count ?? 0,
                    })}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Inventory totals beside the row counts, mirroring the Vaadin header badges.
 *
 * Computed from the rows the table was given. Once paging moves server-side
 * these become server-computed aggregates — the display doesn't change, but the
 * numbers stop being "of what happens to be loaded".
 */
function InventoryBadges({ rows }: { rows: LazyProductDTO[] }) {
  const { t } = useTranslation();

  const totals = useMemo(() => {
    let quantity = 0;
    let value = 0;
    for (const row of rows) {
      quantity += row.kpi_inventory?.quantity ?? 0;
      value += row.kpi_inventory?.inventory_value ?? 0;
    }
    return { quantity, value };
  }, [rows]);

  if (rows.length === 0) return null;

  return (
    <>
      <Badge variant="neutral" size="sm">
        {t("products.badge.units", "Units: {{count}}", { count: totals.quantity })}
      </Badge>
      <Badge variant="neutral" size="sm">
        {t("products.badge.value", "Value")}:{" "}
        {new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: "CAD",
          maximumFractionDigits: 0,
        }).format(totals.value)}
      </Badge>
    </>
  );
}

type ProductsView = "flat" | "families";
const VIEW_KEY = "nexpim.products.view";

function storedView(): ProductsView {
  try {
    return localStorage.getItem(VIEW_KEY) === "families" ? "families" : "flat";
  } catch {
    return "flat";
  }
}

function round(value: number | null | undefined): number | null {
  return value === null || value === undefined ? null : Math.round(value * 100) / 100;
}

/** Two decimals, truncated rather than rounded — matching `FormatUtil`. */
function truncate2(value: number): number {
  return Math.trunc(value * 100) / 100;
}

const MONEY = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function money(value: number | null | undefined): string {
  return value === null || value === undefined ? "0.00" : MONEY.format(value);
}

function formatIsoDate(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
}

/**
 * Creates a product.
 *
 * Type is picked first and never again: it decides which tabs the editor has
 * and how the bundle logic treats the row, so it is fixed at creation — the
 * same constraint the Vaadin dialog imposed. Everything else here is the
 * general and description forms the editor already owns, reused so a product
 * is created and edited through one set of rules.
 */
function CreateProductDialog({
  open,
  onClose,
  client,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  client: ProductClient;
  onCreated: (created: LazyProductDTO) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<ProductDTO>(() => blankProduct("SINGLE_ITEM"));
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!draft.metadata?.sku?.trim()) {
      toast.error(t("form.validation.field.required", "SKU is required"));
      return;
    }
    setSaving(true);
    try {
      const created = await client.create(draft);
      toast.success(t("notification.saved", "Saved"));
      setDraft(blankProduct(draft.type));
      await onCreated(created as unknown as LazyProductDTO);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Create failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("dialog.product-create.title", "Create product")}
      size="lg"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("button.cancel", "Cancel")}
          </Button>
          <Button variant="primary" loading={saving} onClick={() => void submit()}>
            {t("dialog.confirm.create", "Create")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t("column.type", "Type")} required>
          <Select
            value={draft.type}
            options={PRODUCT_TYPES.map((entry) => ({ value: entry, label: entry }))}
            onChange={(event) =>
              setDraft((current) => ({ ...current, type: event.target.value as ProductType }))
            }
          />
        </Field>
        <GeneralTab draft={draft} onChange={setDraft} readOnly={false} />
        <DescriptionsTab draft={draft} onChange={setDraft} readOnly={false} />
      </div>
    </Dialog>
  );
}

/** A minimal product the create form can fill in. */
function blankProduct(type: ProductType): ProductDTO {
  return {
    id: 0,
    type,
    status: "PENDING_LAUNCH",
    acquisition_type: "FABRICATED",
    metadata: { sku: "" },
    description_en: { language: "ENGLISH" },
    description_fr: { language: "FRENCH" },
    pricing: {},
    tags: [],
    collections: [],
  };
}
